import { useRoutes, Navigate } from "react-router-dom";
import { DashboardLayout } from "@/layouts/dashboard/DashboardLayout";

// Auth pages
import BaseAuth from "pages/Auth";
import NewPassword from "pages/Auth/deleteIfnotUse/SignUp/NewPassword";
import ProfileSetup from "pages/Auth/deleteIfnotUse/SignUp/Step/ProfileSetup";
import TelegramCallback from "pages/TelegramCallback";
import CallbackLogin from "pages/Auth/Google/CallbackLogin";

// Admin pages
import NotFound from "pages/NotFound";
import AdminTransactionPage from "@/pages/Admin/Transaction";
import TransactionDetail from "@/pages/Admin/Transaction/TransactionDetail";
import InvestmentTransactionAdmin from "@/pages/Admin/Investment/inventment";
import RevenueManagementPage from "@/pages/Admin/Setting/Revenue";
import SettleDaily from '@/pages/Admin/Setting/SettleDaily';
import CreateAccountsPage from "@/pages/Admin/Accounts/create";
import InvestorManagement from "@/pages/Admin/Investors";
import InvestorDetail from "@/pages/Admin/Investors/InvestorDetail";
import TransferInvestorPage from "@/pages/Admin/Investors/TransferInvestor";
import CommissionPage from "@/pages/Admin/Commission";
import AdminProfilePage from "@/pages/Admin/Profile";
import EditProfileTab from "@/pages/Admin/Profile/EditProfileTab";
import ChangePasswordTab from "@/pages/Admin/Profile/ChangePasswordTab";
import InterestPayment from "@/pages/Admin/InterestPayment";
import InterestPaymentDetail from "@/pages/Admin/InterestPayment/InterestPaymentDetail";
import CreateBatch from "@/pages/Admin/InterestPayment/CreateBatch";
import BatchHistory from "@/pages/Admin/InterestPayment/BatchHistory";
import BatchDetail from "@/pages/Admin/InterestPayment/BatchDetail";
import TransferHistoryPage from "@/pages/Admin/TransferHistory";
import GdkdPage from "@/pages/Admin/GDKD";
import GdkdDetailPage from "@/pages/Admin/GDKD/Detail";
import PlanPackage from "@/pages/Admin/Plan";
import CreateContractData from "@/pages/Admin/Plan/CreateContractData";
import SplitPackagePage from "@/pages/Admin/Plan/SplitPackage";
import PlanDetailPage from "@/pages/Admin/Plan/PlanDetail";
import PlanEditPage from "@/pages/Admin/Plan/PlanEdit";
import Dashboard from "@/pages/Admin/Dashboard";
import ListUserPage from "@/pages/Admin/Accounts/listUser";
import RegisterPackagePage from "@/pages/Admin/Accounts/RegisterPackage";

// Placeholder cho page chưa làm xong
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">{title}</h1>
    <p>This page is under development.</p>
  </div>
);

// Protected Route cho Admin
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const user = localStorage.getItem("user");
  const token = localStorage.getItem("token");

  if (!user || !token) {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Dashboard wrapper cho Admin
const AdminDashboardRoute = () => {
  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  // Redirect users with 'User' role away from admin routes
  if (user?.roles_display === "User") {
    console.warn("You do not have permission to access the admin dashboard.");
    return <Navigate to="/login" replace />;
  }

  return (
    <ProtectedRoute>
      <DashboardLayout user={user || { email: "admin@beqholdings.com" }} />
    </ProtectedRoute>
  );
};

export function AppRoutes() {
  return useRoutes([
    { path: "/", element: <Navigate to="/admin/dashboard" replace /> },

    // Auth Routes - Public
    { path: "/login", element: <BaseAuth routeType="login" /> },
    { path: "/sign-up", element: <BaseAuth routeType="signup" /> },
    { path: "/sign-up/step", element: <ProfileSetup /> },
    {
      path: "/forget-password",
      element: <BaseAuth routeType="forgot-password" />,
    },
    { path: "/verify-otp", element: <BaseAuth routeType="active-account" /> },
    { path: "/reset-password", element: <NewPassword /> },
    { path: "/api/v1/telegram/callback", element: <TelegramCallback /> },
    { path: "/callback-login", element: <CallbackLogin /> },

    // Admin Routes - Protected
    {
      path: "/admin/*",
      element: <AdminDashboardRoute />,
      children: [
        {
          path: "dashboard",
          element: <Dashboard />,
        },
        { path: "analytics", element: <PlaceholderPage title="Analytics" /> },
        { path: "transactions", element: <AdminTransactionPage /> },
        { path: "transactions/:txId", element: <TransactionDetail /> },
        { path: "users", element: <ListUserPage /> },
        { path: "users/create-batch", element: <CreateAccountsPage /> },
        { path: "users/register-package", element: <RegisterPackagePage /> },
        { path: "investors", element: <InvestorManagement /> },
        { path: "gdkd", element: <GdkdPage /> },
        { path: "gdkd/:id", element: <GdkdDetailPage /> },
        { path: "investors/:investorId", element: <InvestorDetail /> },
        { path: "investors/:investorId/transfer", element: <TransferInvestorPage /> },
        { path: "interest-payments", element: <InterestPayment /> },
        { path: "interest-payments/create-batch", element: <CreateBatch /> },
        { path: "interest-payments/:paymentId", element: <InterestPaymentDetail /> },
        { path: "batch-history", element: <BatchHistory /> },
        { path: "batch-history/:batchId", element: <BatchDetail /> },
        { path: "transfer-history", element: <TransferHistoryPage /> },
        { path: "plans", element: <PlanPackage /> },
        { path: "plan/:Id", element: <PlanDetailPage />},
        { path: "plan/:Id/edit", element: <PlanEditPage />},
        { path: "plans/create-contract", element: <CreateContractData /> },
        { path: "plans/split-package", element: <SplitPackagePage /> },
        { path: "investment", element: <InvestmentTransactionAdmin /> },
        { path: "settings/revenue", element: <RevenueManagementPage /> },
        { path: "settings/settle-daily", element: <SettleDaily /> },
        { path: "commissions", element: <CommissionPage /> },
        {
          path: "profile",
          element: <AdminProfilePage />,
          children: [
            { index: true, element: <EditProfileTab /> },
            { path: "change-password", element: <ChangePasswordTab /> },
          ],
        },
      ],
    },

    // 404
    { path: "*", element: <NotFound /> },
  ]);
}
